import { Col, Empty, Row, Tooltip } from 'antd';
import download from 'downloadjs';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { AiOutlineCloudUpload } from 'react-icons/ai';
import { BiTrash } from 'react-icons/bi';
import { BsInfoCircle } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Apis from '../../../../../../api';
import useIsTeamMember from '../../../../../../hooks/useIsTeamMember';
import { HEADERMENU_PATH } from '../../../../../../routes/RoutesURL';
import { RootState } from '../../../../../../store/rootReducer';
import BackDrop from '../../../../../common/BackDrop';
import HSButton from '../../../../../common/Button';
import Confirmation from '../../../../../common/ConfirmationPopup';
import HSDropdown from '../../../../../common/Dropdown';
import NormalInput from '../../../../../common/NormalInput';
import LoginActions from '../../../../../login/redux/actions';
import { KYCDoc, KYCDocType } from '../../../../../login/redux/types';
import { InvestorStatus, UserStatus } from '../../../../../login/utils/Status';
import MessageActions from '../../../../../message/redux/actions';
import KYCSubmitModal from '../../../../settings/modal/KYCSubmitModal';
import KYCThankYou from '../ThankYou';

type Props = {
    isSetting?: boolean;
    onClickCancel?: () => void;
};

const IndividualKYC: React.FC<Props> = ({ isSetting, onClickCancel }) => {
    const isTeamMember: boolean = useIsTeamMember();

    const constants = useSelector((state: RootState) => state.constants.constants);
    const kycInfo = useSelector((state: RootState) => state.user.investor?.kyc);
    const investorStatus = useSelector((state: RootState) => state.user.investor?.status?.kyc);
    const investor = useSelector((state: RootState) => state.user.investor);
    const HIPL_KYC_status = investor?.kyc?.reviewsV2?.find((i) => i.entity === 'HIPL')?.status;
    const showHIPLBanner =
        investor?.isHIPL &&
        (HIPL_KYC_status === InvestorStatus.PendingKYCAdditionalDetails ||
            HIPL_KYC_status === InvestorStatus.PendingKYCSubmission);

    const disabled =
        isTeamMember ||
        (investorStatus !== InvestorStatus.PendingKYCSubmission &&
            investorStatus !== InvestorStatus.PendingKYCAdditionalDetails &&
            investorStatus !== InvestorStatus.PendingKYCResubmission &&
            !showHIPLBanner);

    const [formFields, setFormFields] = useState<FormField>({
        legalName: undefined,
        address: undefined,
        address2: undefined,
        country: undefined,
        idNumber: undefined,
        idType: undefined,
        dob: undefined,
        zipCode: undefined,
        isValidate: false,
    });
    const [isCompleted, setCompleted] = useState(false);

    useLayoutEffect(() => {
        if (kycInfo?.info) {
            setFormFields({
                legalFirstName: kycInfo.info.legalFirstName,
                legalLastName: kycInfo.info.legalLastName,
                address: kycInfo.info.address?.address1,
                address2: kycInfo.info.address?.address2,
                country: kycInfo.info.address?.country,
                idNumber: kycInfo.info.identity?.identityNumber,
                idType: kycInfo.info.identity?.identityType,
                zipCode: kycInfo.info.address?.postalCode,
                residentialStatus: kycInfo.info.residentialStatus,
                nationality: kycInfo.info.nationality,
                dob: kycInfo.info.dob,
                isValidate: false,
            });
        }
        return () => {
            setFormFields({
                legalName: undefined,
                address: undefined,
                address2: undefined,
                country: undefined,
                idNumber: undefined,
                idType: undefined,
                zipCode: undefined,
                residentialStatus: undefined,
                nationality: undefined,
                dob: undefined,
                isValidate: false,
            });
        };
    }, [kycInfo?.info]);

    const isDisable = useMemo(() => {
        const documentCounts: any = {
            'Address Proof': 0,
            'Wealth Proof': 0,
            'Identity Proof': 0,
        };

        if (kycInfo && kycInfo.docs) {
            kycInfo.docs.forEach((d) => {
                if (documentCounts.hasOwnProperty(d.type)) {
                    documentCounts[d.type]++;
                }
            });

            return Object.values(documentCounts).some((count) => count === 0);
        }

        return true;
    }, [kycInfo]);

    const form = useMemo(() => {
        const onFieldChange = (data: { name: string; value?: number | string }) => {
            const { name, value } = data;
            let obj = { ...formFields };
            if (name) {
                obj[name] = (value as string) || '';
            }
            setFormFields(obj);
        };

        const countrys: { label: string; value: string }[] = [];
        constants.countries.forEach((c) => {
            countrys.push({
                label: c.name,
                value: c.name,
            });
        });

        return (
            <div>
                <p className="page_title_onboarding my-4 pt-4">Personal Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <NormalInput
                            title="Legal first name as it appears on NRIC/Passport"
                            isRequired
                            value={formFields.legalFirstName}
                            name="legalFirstName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate &&
                            (!formFields.legalFirstName || formFields.legalFirstName.trim() === '') && (
                                <span className="common_error">Please enter legal first name</span>
                            )}
                    </Col>
                    <Col xs={24}>
                        <NormalInput
                            title="Legal last name as it appears on NRIC/Passport"
                            isRequired
                            value={formFields.legalLastName}
                            name="legalLastName"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate &&
                            (!formFields.legalLastName || formFields.legalLastName.trim() === '') && (
                                <span className="common_error">Please enter legal last name</span>
                            )}
                    </Col>
                    <Col xs={24} md={12}>
                        <HSDropdown
                            label="ID Type"
                            required
                            value={formFields.idType}
                            name="idType"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            options={constants.idTypes}
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.idType || formFields.idType.trim() === '') && (
                            <span className="common_error">Please select ID type</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Identity Number"
                            isRequired
                            value={formFields.idNumber}
                            name="idNumber"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.idNumber || formFields.idNumber.trim() === '') && (
                            <span className="common_error">Please enter identity number</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Date Of Birth"
                            isRequired
                            value={formFields.dob}
                            name="dob"
                            type="date"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.dob || formFields.dob.trim() === '') && (
                            <span className="common_error">Please enter date of birth</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <HSDropdown
                            label="Singapore Residential Status"
                            value={formFields.residentialStatus}
                            name="residentialStatus"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                            options={constants.residentialStatus}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <HSDropdown
                            label="Nationality"
                            required
                            value={formFields.nationality}
                            name="nationality"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                            options={constants.nationalities}
                        />
                        {formFields.isValidate && (!formFields.nationality || formFields.nationality.trim() === '') && (
                            <span className="common_error">Please select nationality</span>
                        )}
                    </Col>
                </Row>
                <p className="page_title_onboarding my-4 pt-4">Address Information</p>
                <Row gutter={[16, 16]}>
                    <Col xs={24}>
                        <NormalInput
                            title="Address line 1"
                            isRequired
                            value={formFields.address}
                            name="address"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.address || formFields.address.trim() === '') && (
                            <span className="common_error">Please enter address</span>
                        )}
                    </Col>
                    <Col xs={24}>
                        <NormalInput
                            title="Address line 2"
                            value={formFields.address2}
                            name="address2"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <HSDropdown
                            label="Country"
                            required
                            value={formFields.country}
                            name="country"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            options={countrys}
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.country || formFields.country.trim() === '') && (
                            <span className="common_error">Please select country</span>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <NormalInput
                            title="Zip code"
                            isRequired
                            type="number"
                            value={formFields.zipCode}
                            name="zipCode"
                            onChange={onFieldChange}
                            className="rounded-xl"
                            disabled={disabled}
                        />
                        {formFields.isValidate && (!formFields.zipCode || formFields.zipCode.trim() === '') && (
                            <span className="common_error">Please enter zip code</span>
                        )}
                    </Col>
                </Row>
            </div>
        );
    }, [formFields, disabled, constants]);

    return (
        <div>
            {!isCompleted ? (
                <>
                    {form}
                    <p className="page_title_onboarding my-4 py-4">KYC Documents</p>

                    <Documents disabled={disabled} isSetting={isSetting} />

                    <p className="text-sm italic !font-[Lato]">
                        Please note that all documents are required to be Certified as a True Copy. Acceptable
                        certifiers include notary, lawyers and certified public accountants. The certifying individual
                        is to state his/her name, certifying capacity, license/membership number and date of
                        certification (within the last 3 months).
                    </p>

                    <div
                        className={`flex justify-center items-center mt-4 mb-5 ${isSetting ? 'flex-row' : 'flex-col'}`}
                    >
                        {!disabled && (
                            <KYCSubmitModal
                                isDisable={isDisable}
                                // className="onboarding_btn"
                                onSubmitCallback={() => {
                                    if (!isSetting) {
                                        setCompleted(true);
                                    } else if (onClickCancel) {
                                        onClickCancel();
                                    }
                                }}
                                kycInfo={formFields}
                                setFormFields={setFormFields}
                            />
                        )}
                        {isSetting && onClickCancel && (
                            <HSButton className="onboarding_btn ml-2" light title="Cancel" onClick={onClickCancel} />
                        )}
                        {!isSetting && <SkipKYC disabled={disabled} />}
                    </div>
                </>
            ) : (
                <KYCThankYou />
            )}
        </div>
    );
};

export default IndividualKYC;
type SkipKYCProps = {
    disabled: boolean;
};
export const SkipKYC: React.FC<SkipKYCProps> = ({ disabled }) => {
    const navigate = useNavigate();
    const userStatus = useSelector((state: RootState) => state.user.user?.status);
    return (
        <HSButton
            type="link"
            title={!disabled ? 'Skip KYC for now' : 'View Deals'}
            onClick={() => {
                if (userStatus && userStatus === UserStatus.pendingActivation) {
                    navigate(HEADERMENU_PATH.accountActivationWithOtp);
                } else {
                    navigate(HEADERMENU_PATH.deals);
                }
            }}
            className="mt-0 underline color-1B3E8C hover:underline skip relative -top-3"
        />
    );
};

export const DocTooltip = {
    identityProof: [
        "A certified true copy of the individual's passport or identity card containing a clear photo of the individual, and showing the individual's full name, unique identification number, date of birth and nationality",
    ],
    adreesProof: [
        "Documentary proof of the individual's address including Local utility/phone bill or local bank statement",
    ],
    wealthProof: [
        'Example of documents to prove wealth: ',
        'a. Title deeds to property',
        'b. Bank account statements (Last 3 months)',
        'c. Statement of holdings from brokers or fund managers',
    ],
};

const Documents = ({ disabled, isSetting }: { disabled: boolean; isSetting?: boolean }) => {
    const kycDocs = useSelector((state: RootState) => state.user.investor?.kyc?.docs);

    let identityDocs: KYCDoc[] = [],
        addressDocs: KYCDoc[] = [],
        wealthDocs: KYCDoc[] = [];

    if (kycDocs && kycDocs.length > 0) {
        identityDocs = kycDocs.filter((k) => k.type === 'Identity Proof');
        addressDocs = kycDocs.filter((k) => k.type === 'Address Proof');
        wealthDocs = kycDocs.filter((k) => k.type === 'Wealth Proof');
    }

    return (
        <div className="mb-4">
            <div>
                <DocSection
                    title="Proof of identity (Certified True Copy)"
                    docs={identityDocs}
                    tooltip={DocTooltip.identityProof}
                    docType="Identity Proof"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Proof of address (Certified True Copy)"
                    docs={addressDocs}
                    tooltip={DocTooltip.adreesProof}
                    docType="Address Proof"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
                <DocSection
                    title="Proof of Income or Net Assets"
                    docs={wealthDocs}
                    tooltip={DocTooltip.wealthProof}
                    docType="Wealth Proof"
                    disabled={disabled}
                    required
                    isSetting={isSetting}
                />
            </div>
        </div>
    );
};

type DocSectionProps = {
    title: string;
    docs: KYCDoc[];
    tooltip?: string[];
    docType: KYCDocType;
    disabled?: boolean;
    required?: boolean;
    isSetting?: boolean;
};
export const DocSection: React.FC<DocSectionProps> = ({ title, docs, tooltip, docType, disabled, required }) => {
    const dispatch = useDispatch();
    const [loader, setLoader] = useState<string | undefined>(undefined);
    const investorUUID = useSelector((state: RootState) => state.user.investor?.uuid);
    const [onDeleteConfirm, setOnDeleteConfirm] = useState(false);
    const [deleteloader, setDeleteLoader] = useState<string | undefined>(undefined);

    const onDownload = (doc: KYCDoc) => {
        if (investorUUID) {
            setLoader(doc.uuid);
            Apis.KYCDocumentDownload(investorUUID, doc.uuid)
                .then((res) => {
                    download(
                        new Blob([res.data], { type: res.headers['content-type'] }),
                        doc.title,
                        res.headers['content-type'],
                    );
                })
                .finally(() => {
                    setLoader(undefined);
                });
        }
    };

    const onUpload = (file: File) => {
        if (investorUUID) {
            let data = new FormData();
            data.set('entityId', investorUUID);
            data.set('title', file.name);
            data.set('docType', docType);
            data.append('doc', file);
            setLoader(docType);
            Apis.uploadKYCDocument(investorUUID, data)
                .then((res) => {
                    dispatch(LoginActions.updateKYCInfo(res.data.kyc));
                    // if (isSetting) {
                    //     dispatch(MessageActions.showMessage({ error: false, text: 'Document uploaded successfully' }));
                    // }
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setLoader(undefined);
                });
        }
    };

    const onDelete = (doc: KYCDoc) => {
        if (investorUUID) {
            setDeleteLoader(`download_${doc.uuid}`);
            Apis.deleteKYCDoc(investorUUID, doc.uuid)
                .then((res) => {
                    dispatch(LoginActions.updateKYCInfo(res.data.kyc));
                    // if (isSetting) {
                    //     dispatch(
                    //         MessageActions.showMessage({ error: false, text: 'KYC Document deleted successfully!' }),
                    //     );
                    // }
                })
                .catch((err) => {
                    dispatch(MessageActions.showMessage({ error: true, text: String(err) }));
                })
                .finally(() => {
                    setOnDeleteConfirm(false);
                    setDeleteLoader(undefined);
                });
        }
    };

    return (
        <div className="mb-4">
            <div
                className="bg-[#E4E2DA] h-auto flex items-center px-4 py-1 justify-between "
                style={{ borderBottom: `1px solid #C4CFE4` }}
            >
                <div className={`flex items-center text-[#20222B] WorkSans ${disabled ? 'py-1' : ''}`}>
                    <span>
                        {title} {required && <span className="text-red-500">*</span>}
                        {tooltip && (
                            <Tooltip
                                title={
                                    <>
                                        {tooltip.map((t) => (
                                            <span key={t.substring(0, 10)}>
                                                {t} <br />
                                            </span>
                                        ))}
                                    </>
                                }
                            >
                                <span className="ml-1 mr-4 inline-block relative top-0.5 color-182D4F">
                                    <BsInfoCircle color="#26AE60" />
                                </span>
                            </Tooltip>
                        )}
                    </span>
                </div>

                {!disabled && (
                    <div className="p-2 rounded-xl select-none">
                        {loader !== docType && (
                            <label htmlFor={docType} className="cursor-pointer color-182D4F">
                                <AiOutlineCloudUpload size="1.4rem" color="#26AE60" />
                            </label>
                        )}
                        {loader === docType && <BackDrop />}
                        <input
                            id={docType}
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    const file: File = e.target.files[0];
                                    onUpload(file);
                                    e.target.value = '';
                                }
                            }}
                        />
                    </div>
                )}
            </div>
            <div className="px-4 py-1 bg-white rounded-b-md">
                {docs.map((doc) => (
                    <div className="h-8 flex items-center justify-between w-full" key={doc.uuid}>
                        <div className="flex items-center  select-none w-full">
                            <p
                                className="ml-1 mr-3 hover:underline w-auto break-all cursor-pointer text-[#20222B] font-semibold WorkSans"
                                onClick={() => onDownload(doc)}
                            >
                                {doc.title}
                            </p>
                            {loader && loader === doc.uuid && <BackDrop />}
                        </div>

                        {!disabled && loader !== `download_${doc.uuid}` && (
                            <div
                                onClick={() => setOnDeleteConfirm(true)}
                                className="hover:bg-gray-100 rounded-lg cursor-pointer select-none p-2 color-182D4F"
                            >
                                <BiTrash size="1.2rem" color="#26AE60" />
                            </div>
                        )}

                        <Confirmation
                            title="Delete KYC Doc"
                            message={'Do you want to proceed with this action?'}
                            visible={onDeleteConfirm}
                            onCancel={() => setOnDeleteConfirm(false)}
                            onOk={() => onDelete(doc)}
                            loader={deleteloader === `download_${doc.uuid}`}
                        />

                        {loader === `download_${doc.uuid}` && <BackDrop />}
                    </div>
                ))}
                {docs.length === 0 && (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        className="m-0 mt-1"
                        imageStyle={{ height: 25 }}
                        description={<span className="text-sm">No Data</span>}
                    />
                )}
            </div>
        </div>
    );
};

interface FormField {
    [k: string]: string | boolean | undefined;
    legalFirstName?: string;
    legalLastName?: string;
    idType?: string;
    idNumber?: string;
    address?: string;
    address2?: string;
    country?: string;
    zipCode?: string;
    isValidate: boolean;
    residentialStatus?: string;
    nationality?: string;
    dob?: string;
}
